import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ShieldCheck, Truck, RotateCcw, BadgeCheck, HeadphonesIcon, Search,
  Star, ArrowRight, GraduationCap, Briefcase, Wrench, Stethoscope, Scale,
  Cpu, Baby, ScrollText, Sparkles, BookMarked, Church, Palette, School, ClipboardCheck,
  Zap, DollarSign, Package, Repeat, MessageCircle, BookOpen,
} from "lucide-react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import ProductCard from "@/components/store/ProductCard";

const CATEGORIES = [
  { name: "Academic",         icon: GraduationCap, slug: "academic",         color: "bg-blue-50 text-blue-700" },
  { name: "Business",         icon: Briefcase,     slug: "business",         color: "bg-amber-50 text-amber-700" },
  { name: "Engineering",      icon: Wrench,        slug: "engineering",      color: "bg-slate-100 text-slate-700" },
  { name: "Medical",          icon: Stethoscope,   slug: "medical",          color: "bg-red-50 text-red-700" },
  { name: "Law",              icon: Scale,         slug: "law",              color: "bg-indigo-50 text-indigo-700" },
  { name: "Computer Science", icon: Cpu,           slug: "computer-science", color: "bg-cyan-50 text-cyan-700" },
  { name: "Children",         icon: Baby,          slug: "children",         color: "bg-pink-50 text-pink-700" },
  { name: "History",          icon: ScrollText,    slug: "history",          color: "bg-orange-50 text-orange-700" },
  { name: "Self Help",        icon: Sparkles,      slug: "self-help",        color: "bg-emerald-50 text-emerald-700" },
  { name: "Fiction",          icon: BookMarked,    slug: "fiction",          color: "bg-purple-50 text-purple-700" },
  { name: "Religion",         icon: Church,        slug: "religion",         color: "bg-yellow-50 text-yellow-700" },
  { name: "Arts",             icon: Palette,       slug: "arts",             color: "bg-fuchsia-50 text-fuchsia-700" },
  { name: "Education",        icon: School,        slug: "education",        color: "bg-teal-50 text-teal-700" },
  { name: "Exam Preparation", icon: ClipboardCheck,slug: "exam-preparation", color: "bg-lime-50 text-lime-700" },
];

const WHY_BUY = [
  { icon: Zap,              title: "Prompt Processing",       desc: "Orders processed within 2–4 business days and shipped from the United States." },
  { icon: ShieldCheck,      title: "Secure Payments",         desc: "SSL-encrypted checkout, all major cards accepted." },
  { icon: DollarSign,       title: "Competitive Prices",      desc: "Competitive pricing on new books and textbooks." },
  { icon: Package,          title: "Wide Selection",          desc: "A broad catalog of academic and general reading titles." },
  { icon: Repeat,           title: "Easy Returns",            desc: "15-day returns on eligible items." },
  { icon: MessageCircle,    title: "Customer Support",        desc: "Email support Monday to Friday, response within 1–2 business days." },
  { icon: BadgeCheck,       title: "New Books",               desc: "We sell brand-new books sourced from our suppliers." },
];

const REVIEWS = [];
// Reviews section removed — we do not display fabricated testimonials.
// If genuine customer reviews are collected via a verified provider (e.g. Trustpilot),
// they can be added back here.

function TrustBar() {
  const items = [
    { icon: ShieldCheck,       label: "Secure Checkout" },
    { icon: Truck,             label: "Fast Shipping" },
    { icon: RotateCcw,         label: "Easy Returns" },
    { icon: BadgeCheck,        label: "Authentic Books" },
    { icon: HeadphonesIcon,    label: "USA Customer Support" },
  ];
  return (
    <div className="bg-white border-y border-slate-200" data-testid="trust-bar">
      <div className="container-book py-4 grid grid-cols-2 md:grid-cols-5 gap-4">
        {items.map((i) => (
          <div key={i.label} className="trust-item justify-center md:justify-start">
            <i.icon size={18} className="text-primary shrink-0" />
            <span className="text-sm">{i.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Hero() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  return (
    <section
      className="relative isolate overflow-hidden"
      data-testid="hero-section"
      style={{
        backgroundImage: "url('https://images.unsplash.com/photo-1693921398753-c5d114e8ae6a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwxfHx1bml2ZXJzaXR5JTIwbGlicmFyeSUyMHNoZWx2ZXN8ZW58MHx8fHwxNzg1MjI1NjI4fDA&ixlib=rb-4.1.0&q=85')",
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <div className="absolute inset-0 hero-overlay" aria-hidden="true" />
      <div className="relative container-book py-20 md:py-32 grid gap-12 lg:grid-cols-[1.3fr,1fr] items-center">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 backdrop-blur text-white text-xs font-medium border border-white/25"
          >
            <BadgeCheck size={13} className="text-accent" /> New books · Fast U.S. shipping · Secure checkout
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.05 }}
            className="mt-5 font-heading text-white font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.05]"
            data-testid="hero-headline"
          >
            Your Trusted Online Bookstore for <span className="text-accent">New &amp; Academic Books</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.12 }}
            className="mt-5 text-lg text-white/90 max-w-xl"
          >
            Over thousands of titles with fast delivery across the USA. Textbooks, professional
            references, and educational materials at unbeatable prices.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }}
            className="mt-8 flex flex-wrap gap-3"
          >
            <Link
              to="/books"
              className="inline-flex items-center gap-2 px-6 py-3.5 bg-primary hover:bg-primary/90 text-white rounded-xl font-semibold shadow-lg shadow-primary/25 transition-all hover:-translate-y-0.5"
              data-testid="hero-shop-now"
            >
              Shop Now <ArrowRight size={18} />
            </Link>
            <a
              href="#categories"
              className="inline-flex items-center gap-2 px-6 py-3.5 bg-white/10 backdrop-blur hover:bg-white text-white hover:text-secondary border border-white/30 rounded-xl font-semibold transition-all"
              data-testid="hero-browse-categories"
            >
              Browse Categories
            </a>
          </motion.div>
        </div>

        {/* Prominent search card */}
        <motion.div
          initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5, delay: 0.15 }}
          className="w-full bg-white rounded-2xl shadow-lift p-6 md:p-7"
          data-testid="hero-search-card"
        >
          <h3 className="font-heading font-semibold text-xl text-slate-900">Find your book instantly</h3>
          <p className="text-slate-500 text-sm mt-1">Search by ISBN, title, author, or publisher.</p>
          <form onSubmit={(e) => { e.preventDefault(); if (q.trim()) navigate(`/books?q=${encodeURIComponent(q.trim())}`); }} className="mt-4 flex gap-2">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="978-0-13-468-599-1 · Introduction to Algorithms · CLRS"
                className="pl-10 h-12 border-slate-200 rounded-xl focus-visible:ring-2 focus-visible:ring-primary"
                data-testid="hero-search-input"
              />
            </div>
            <Button type="submit" className="h-12 rounded-xl bg-primary hover:bg-secondary text-white font-semibold px-5" data-testid="hero-search-btn">
              Search
            </Button>
          </form>
          <div className="mt-4 flex flex-wrap gap-2">
            <span className="text-xs text-slate-500 font-medium">Popular:</span>
            {["CLRS", "Gray's Anatomy", "Calculus 8e", "Kaplan MCAT"].map((s) => (
              <Link key={s} to={`/books?q=${encodeURIComponent(s)}`} className="text-xs text-primary hover:text-secondary underline underline-offset-2">{s}</Link>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function Categories({ items, apiCats }) {
  // Use API categories as source of truth for real navigation; overlay premium visuals.
  const list = (apiCats && apiCats.length ? apiCats.map((name) => {
    const match = CATEGORIES.find((c) => c.name.toLowerCase() === name.toLowerCase());
    return match || { name, slug: name, icon: BookOpen, color: "bg-slate-100 text-slate-700" };
  }) : items);

  return (
    <section id="categories" className="container-book py-16 md:py-20" data-testid="categories-section">
      <div className="flex items-end justify-between mb-8">
        <div>
          <p className="text-primary font-semibold text-sm mb-1">Explore</p>
          <h2 className="font-heading font-bold text-3xl md:text-4xl text-slate-900">Shop by category</h2>
        </div>
        <Link to="/books" className="hidden md:inline-flex items-center gap-1 text-primary font-medium text-sm hover:gap-2 transition-all">
          Browse all <ArrowRight size={14} />
        </Link>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3">
        {list.slice(0, 14).map((c, i) => {
          const Icon = c.icon || BookOpen;
          const slugForUrl = c.name; // use display name — matches API filter
          return (
            <motion.div
              key={c.name}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.3, delay: i * 0.03 }}
            >
              <Link
                to={`/shop?category=${encodeURIComponent(slugForUrl)}`}
                data-testid={`home-category-${(c.slug || c.name).toLowerCase().replace(/\s+/g, "-")}`}
                className="group flex flex-col items-center text-center p-4 md:p-5 bg-white border border-slate-200 rounded-xl hover:border-primary hover:-translate-y-1 hover:shadow-lift transition-all h-full"
              >
                <div className={`w-12 h-12 md:w-14 md:h-14 rounded-xl ${c.color || "bg-slate-100 text-slate-700"} flex items-center justify-center mb-3 group-hover:scale-110 transition-transform`}>
                  <Icon size={22} />
                </div>
                <span className="text-sm font-semibold text-slate-800 group-hover:text-primary">{c.name}</span>
              </Link>
            </motion.div>
          );
        })}
      </div>
    </section>
  );
}

function FeaturedProducts({ books, loading }) {
  return (
    <section className="bg-white border-y border-slate-200" data-testid="featured-section">
      <div className="container-book py-16 md:py-20">
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="text-primary font-semibold text-sm mb-1">Bestsellers</p>
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-slate-900">Featured this week</h2>
          </div>
          <Link to="/books?featured=1" className="hidden md:inline-flex items-center gap-1 text-primary font-medium text-sm hover:gap-2 transition-all">
            Shop all featured <ArrowRight size={14} />
          </Link>
        </div>
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i}><div className="skeleton aspect-book" /><div className="skeleton h-4 mt-3 w-3/4" /><div className="skeleton h-3 mt-2 w-1/2" /></div>
            ))}
          </div>
        ) : books.length === 0 ? (
          <div className="py-10 text-center border border-dashed border-slate-200 rounded-2xl">
            <p className="text-slate-500 text-sm">No featured titles right now. New arrivals ship weekly.</p>
            <Link to="/books" className="mt-3 inline-block text-primary font-semibold text-sm hover:text-secondary">Browse all books →</Link>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-6">
            {books.slice(0, 10).map((b, i) => <ProductCard key={b.id} book={b} index={i} />)}
          </div>
        )}
      </div>
    </section>
  );
}

function WhyBuy() {
  return (
    <section className="container-book py-16 md:py-20" data-testid="why-buy-section">
      <div className="text-center mb-12">
        <p className="text-primary font-semibold text-sm mb-1">Why choose us</p>
        <h2 className="font-heading font-bold text-3xl md:text-4xl text-slate-900">Built for readers, backed by trust</h2>
        <p className="text-slate-600 mt-3 max-w-2xl mx-auto">Every order is carefully prepared, inspected, and shipped from the United States.</p>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
        {WHY_BUY.map((w) => (
          <div key={w.title} className="text-center p-5 bg-white border border-slate-200 rounded-xl hover:shadow-lift hover:-translate-y-0.5 transition-all">
            <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary mx-auto flex items-center justify-center mb-3">
              <w.icon size={22} />
            </div>
            <h3 className="font-semibold text-sm text-slate-900">{w.title}</h3>
            <p className="text-xs text-slate-500 mt-1 leading-relaxed">{w.desc}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function Reviews() {
  return null;
}

function CTABand() {
  return (
    <section className="container-book py-16 md:py-24">
      <div className="rounded-2xl bg-secondary text-white p-10 md:p-14 grid gap-8 md:grid-cols-2 items-center overflow-hidden relative">
        <div className="relative z-10">
          <h2 className="font-heading font-bold text-3xl md:text-4xl">Have an ISBN? Get your book in three clicks.</h2>
          <p className="mt-3 text-white/85 text-lg">Paste any ISBN into our search and we'll ship it to your door with tracking, in original publisher condition.</p>
        </div>
        <div className="relative z-10 flex flex-col sm:flex-row gap-3 md:justify-end">
          <Link to="/books" className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-accent text-secondary hover:bg-accent/90 rounded-xl font-semibold transition-all">
            Start shopping <ArrowRight size={18} />
          </Link>
          <Link to="/about" className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-white/10 hover:bg-white hover:text-secondary text-white border border-white/25 rounded-xl font-semibold transition-all">
            Learn about us
          </Link>
        </div>
        <BookOpen size={340} className="absolute -right-10 -bottom-10 text-white/5" strokeWidth={1} aria-hidden="true" />
      </div>
    </section>
  );
}

export default function Home() {
  const [books, setBooks] = useState([]);
  const [featLoading, setFeatLoading] = useState(true);
  const [apiCategories, setApiCategories] = useState([]);

  useEffect(() => {
    setFeatLoading(true);
    api.get("/books/featured-or-random", { params: { limit: 8 } })
      .then((r) => setBooks(r.data || []))
      .catch(() => setBooks([]))
      .finally(() => setFeatLoading(false));
    api.get("/books/categories").then((r) => setApiCategories(r.data || [])).catch(() => {});
  }, []);

  // Show design-driven categories that ALSO exist in the API, otherwise fall back to whatever the catalog exposes
  const apiSet = new Set(apiCategories.map((c) => (c || "").toLowerCase()));
  const displayCategories = CATEGORIES.filter((c) =>
    apiSet.has(c.name.toLowerCase()) || apiSet.has(c.slug) || apiSet.size === 0
  );
  const finalCats = displayCategories.length > 0 ? displayCategories : CATEGORIES;

  return (
    <div data-testid="home-page">
      <Hero />
      <TrustBar />
      <Categories items={finalCats} apiCats={apiCategories} />
      <FeaturedProducts books={books} loading={featLoading} />
      <WhyBuy />
      {/* Reviews section removed for compliance with Google Merchant Center misrepresentation policy. */}
      <CTABand />
    </div>
  );
}
